import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLang } from '../i18n/LanguageContext';
import { useCart } from '../contexts/CartContext';
import GamifiedButton from '../components/common/GamifiedButton';
import GameProgress from '../components/common/GameProgress';
import { getProducts } from '../services/api';

export default function Cart() {
  const { t } = useLang();
  const { cart, removeFromCart, removeFromCartAt, subtotal, discount, discountAmount, total, applyDiscount, removeDiscount, updateCartItemLanguage } = useCart();
  const [products, setProducts] = React.useState([]);
  const [lignesRetirees, setLignesRetirees] = React.useState([]);

  React.useEffect(() => {
    getProducts().then(data => {
      if (Array.isArray(data)) setProducts(data);
    }).catch(err => console.error('Error fetching products for weight sync:', err));
  }, []);

  // Le panier vit dans localStorage : il survit à la disparition d'un pack.
  // Sans ce nettoyage, un visiteur revenant avec un ancien pack se heurte à un
  // « produit introuvable » au paiement, sans aucun moyen de comprendre.
  //
  // On ne purge que si le catalogue a VRAIMENT été reçu : getProducts renvoie
  // [] quand l'API est en panne, et vider le panier dans ce cas serait pire que
  // le problème.
  React.useEffect(() => {
    if (products.length === 0) return;

    const idsConnus = new Set(products.map(p => String(p.id)));
    const obsoletes = cart.filter(item => !idsConnus.has(String(item.id)));
    if (obsoletes.length === 0) return;

    obsoletes.forEach(item => removeFromCart(item.id));
    setLignesRetirees(prev => [...prev, ...obsoletes.map(i => i.name)]);
  }, [products, cart, removeFromCart]);

  // Helper to get weight for an item
  const getItemWeight = (itemId, fallbackWeight) => {
    const product = products.find(p => p.id === itemId);
    return product ? parseFloat(product.weight) : (fallbackWeight || 1.5);
  };
  const [promoCode, setPromoCode] = React.useState('');
  const [promoError, setPromoError] = React.useState(null);
  const [promoSuccess, setPromoSuccess] = React.useState(null);

  const handleApplyPromo = async (e) => {
    e.preventDefault();
    setPromoError(null);
    setPromoSuccess(null);

    if (!promoCode.trim()) return;

    try {
      const applied = await applyDiscount(promoCode.toUpperCase());
      setPromoSuccess(t('tunnel.panier.promoAppliquee', { code: applied.code }));
      setPromoCode('');
    } catch (err) {
      setPromoError(err.message);
    }
  };

  // Par RANG : deux lignes du même pack ne se distinguent que par leur
  // position (variante Garçon / Fille, langue de boîte FR / NL).
  const handleRemove = (index) => {
    removeFromCartAt(index);
  };

  return (
    <div className="min-h-[calc(100vh-70px)] bg-gradient-to-br from-background via-surface to-cream py-15 px-5">
      <SEO
        title={t('tunnel.panier.seoTitre')}
        description={t('tunnel.panier.seoDescription')}
        url="/cart"
        keywords="panier, checkout, achat puzzle révélation, commande"
        noindex
      />

      <div className="max-w-[1000px] mx-auto">
        {/* Header */}
        <div className="text-center mb-15 animate-[fadeInUp_0.8s_ease-out]">
          <h1 className="m-0 text-3xl sm:text-4xl md:text-5xl font-black text-text uppercase tracking-tight mb-3 break-words">
            {t('tunnel.panier.titre')}
          </h1>
          <p className="m-0 text-base text-text-light font-medium">
            {cart.length === 0
              ? t('tunnel.panier.sousTitreVide')
              : cart.length > 1
                ? t('tunnel.panier.sousTitrePlusieurs', { n: cart.length })
                : t('tunnel.panier.sousTitreUn', { n: cart.length })
            }
          </p>
        </div>

        {/* Articles retirés parce qu'ils ne sont plus au catalogue */}
        {lignesRetirees.length > 0 && (
          <div
            role="status"
            className="mb-8 rounded-xl border-2 border-[#EDB82F] bg-[#FFF8E6] p-5 text-[#33403B]"
          >
            <p className="m-0 mb-1 text-sm font-bold">
              {lignesRetirees.length > 1
                ? t('tunnel.panier.retireTitrePlusieurs')
                : t('tunnel.panier.retireTitreUn')}
            </p>
            <p className="m-0 text-[13px] leading-relaxed">
              {t('tunnel.panier.retireDetail', { noms: lignesRetirees.join(', ') })}{' '}
              <Link to="/shop" className="font-bold text-[#2F7E72] underline">{t('tunnel.panier.retireDetailLien')}</Link>.
            </p>
          </div>
        )}

        {/* Progress */}
        {cart.length > 0 && (
          <div className="mb-15 max-w-[600px] mx-auto animate-[fadeInUp_0.8s_ease-out_0.2s_backwards]">
            {/* Le panier est la première des trois étapes de la commande.
                Il affichait « Collection en Cours 1/24 · 4 % · Plus que 23
                pièces à découvrir » : le compteur du puzzle (24 pièces)
                appliqué à des boîtes, juste au-dessus du bouton de paiement. */}
            <GameProgress
              current={1}
              total={3}
              label={t('tunnel.panier.etape')}
              messageRestant={t('progression.etapesRestantes', { n: 2 })}
            />
          </div>
        )}

        {cart.length === 0 ? (
          /* Empty Cart */
          <div className="text-center py-20 px-10 bg-white border-2 border-dashed border-primary rounded-lg animate-[fadeInUp_0.8s_ease-out] shadow-soft">
            <div className="text-[64px] mb-5 inline-block animate-[float_3s_ease-in-out_infinite]">
              🎲
            </div>
            <h2 className="m-0 mb-4 text-[32px] text-text uppercase tracking-tight">
              {t('tunnel.panier.videTitre')}
            </h2>
            <p className="m-0 mb-8 text-base text-text-light max-w-[500px] mx-auto">
              {t('tunnel.panier.videTexte')}
            </p>
            <Link to="/" className="inline-block no-underline">
              <GamifiedButton variant="primary" size="lg">
                {t('tunnel.panier.videCta')}
              </GamifiedButton>
            </Link>
          </div>
        ) : (
          /* Cart Items */
          <>
            <div className="flex flex-col gap-5 mb-10 animate-[fadeInUp_0.8s_ease-out_0.2s_backwards]">
              {cart.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white border-2 border-primary/20 rounded-lg p-5 transition-all duration-300 gap-5 hover:border-primary hover:shadow-lg"
                >
                  {/* Item Content */}
                  <div className="flex gap-5 flex-1 items-center w-full md:w-auto flex-col md:flex-row">
                    <div className="w-[100px] h-[100px] rounded-lg overflow-hidden border-2 border-primary flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 flex flex-col gap-2">
                      <h3 className="m-0 text-base font-semibold text-text uppercase tracking-wide">
                        {item.name}
                      </h3>
                      <p className="m-0 text-base font-bold text-primary">
                        💎 {item.price ? Number(item.price).toFixed(2) : '0.00'} €
                      </p>
                      <div className="flex gap-4 text-xs text-text-light">
                        <span className="flex items-center gap-1">
                          ⚖️ {getItemWeight(item.id, item.weight)}kg
                        </span>
                      </div>

                      {/* Language Toggle FR/NL */}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-text-light font-medium">{t('tunnel.panier.langue')}</span>
                        <div className="flex gap-1">
                          <button
                            data-cta="panier:boite-fr"
                            onClick={() => updateCartItemLanguage(index, 'FR')}
                            className={`px-3 py-1 text-xs font-bold rounded transition-all ${(item.language || 'FR') === 'FR'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            🇫🇷 FR
                          </button>
                          <button
                            data-cta="panier:boite-nl"
                            onClick={() => updateCartItemLanguage(index, 'NL')}
                            className={`px-3 py-1 text-xs font-bold rounded transition-all ${item.language === 'NL'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            🇳🇱 NL
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(index)}
                    className="bg-red-500 hover:bg-red-600 text-white border-none w-10 h-10 rounded-full text-xl font-bold cursor-pointer transition-all duration-300 flex items-center justify-center flex-shrink-0 self-end md:self-center hover:scale-110 active:scale-95"
                    title={t('tunnel.panier.retirerDuPanier')}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-white border-2 border-primary/20 rounded-lg p-8 mb-10 animate-[fadeInUp_0.8s_ease-out_0.3s_backwards] shadow-soft">
              <div className="flex justify-between items-center mb-4 text-sm">
                <span className="text-text font-semibold uppercase tracking-wide">
                  {t('tunnel.panier.sousTotal')}
                </span>
                <span className="text-text font-semibold">
                  {subtotal.toFixed(2)} €
                </span>
              </div>

              {discount && (
                <div className="flex justify-between items-center mb-4 text-sm">
                  <span className="text-text font-semibold uppercase tracking-wide">
                    {t('tunnel.panier.reduction', { code: discount.code })}
                    <button
                      onClick={removeDiscount}
                      className="ml-2 text-xs bg-transparent border-none text-red-500 cursor-pointer hover:text-red-700"
                      title={t('tunnel.panier.retirer')}
                    >
                      ✕
                    </button>
                  </span>
                  <span className="text-text font-semibold">
                    - {discountAmount.toFixed(2)} €
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center mb-4 text-sm">
                <span className="text-text font-semibold uppercase tracking-wide">
                  {t('tunnel.panier.livraison')}
                </span>
                <span className="text-text-light font-semibold">
                  {t('tunnel.panier.livraisonADeterminer')}
                </span>
              </div>

              {/* Promo Code Input */}
              <div className="mb-4">
                <form onSubmit={handleApplyPromo} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder={t('tunnel.panier.codePromo')}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="flex-1 min-w-0 bg-surface border-2 border-primary/20 text-text px-3 py-2 rounded text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                  <button
                    type="submit"
                    className="bg-white border-2 border-primary hover:bg-primary hover:text-white text-black px-4 py-2 rounded font-semibold text-sm cursor-pointer transition-all duration-300"
                  >
                    {t('tunnel.panier.appliquer')}
                  </button>
                </form>
                {promoError && (
                  <div className="text-red-400 text-xs mt-1">{promoError}</div>
                )}
                {promoSuccess && (
                  <div className="text-green-400 text-xs mt-1">{promoSuccess}</div>
                )}
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent my-4"></div>

              <div className="flex justify-between items-center text-lg font-black">
                <span className="text-text">{t('tunnel.panier.total')}</span>
                <span className="text-primary text-2xl">
                  {total.toFixed(2)} €
                </span>
              </div>

              {/* Special Offer */}
              {cart.length >= 5 && (
                <div className="flex items-center gap-3 bg-pink/10 border-2 border-pink rounded-lg p-4 mt-5">
                  <span className="text-2xl inline-block">✨</span>
                  <span className="text-text text-sm font-semibold">
                    {t('tunnel.panier.offreSpeciale')}
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-[fadeInUp_0.8s_ease-out_0.4s_backwards]">
              <Link to="/" className="no-underline">
                <GamifiedButton variant="secondary" size="lg">
                  {t('tunnel.panier.continuerCollecter')}
                </GamifiedButton>
              </Link>

              <Link to="/checkout" className="no-underline">
                <GamifiedButton variant="primary" size="lg" data-cta="cart:checkout">
                  {t('tunnel.panier.procederPaiement')}
                </GamifiedButton>
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
}
